import { useTranslation } from "react-i18next";

import { Plus, SlEsim } from "@/assets/icons/icons";
import { ButtonLink } from "@/components/Interface/Button/Button";
import { TabLayout } from "@/components/Layout/BaseLayouts/TabLayout";
import { PortalHeading } from "@/components/Portal/components/PortalHeading/PortalHeading";
import { PortalSearchAndFilterMenu } from "@/components/Portal/components/PortalSearchAndFilterMenu/PortalSearchAndFilterMenu";
import { useIsMobile } from "@/hooks/useIsMobile";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetSimList } from "@/services/api";
import { FCC } from "@/types";

/**
 * Shared shell of the Abos page: the ivory heading card with the "Abo
 * hinzufügen" action, the search/filter/view-toggle bar, and below it the
 * currently selected representation of the subscriptions - the tile grid or
 * the table.
 */
export const MySubscriptionsLayout: FCC = ({ children }) => {
  const { t } = useTranslation("portal");

  // The header action is the design system's large button on mobile (40px) and
  // the small one from tablet up (32px) - see the Abos frames in Figma.
  const isMobile = useIsMobile();

  // Unfiltered total, independent of the list/tiles views' own search-and-filter
  // query, so the bar doesn't disappear while someone is typing into it and the
  // filtered result count drops to <=1. Search and filter only make sense with
  // more than one subscription.
  const { data: simData } = useGetSimList(
    { pageSize: 1 },
    { query: { staleTime: 60 * 1000 } },
  );
  // Default to visible while data is unavailable (still loading, or the
  // request failed) - only a confirmed totalElements of 0 or 1 hides it.
  // Defaulting to hidden here made the bar flash hidden-then-shown on every
  // load for anyone with more than one subscription, and stay wrongly
  // hidden for good if this request errored.
  const showSearchAndFilter =
    simData?.totalElements === undefined || simData.totalElements > 1;

  return (
    <TabLayout
      header={
        <PortalHeading
          heading={t("my-subscriptions.heading")}
          icon={<SlEsim />}
          action={
            <ButtonLink
              data-testid="add-device-button"
              to={portalRoutes.addDevice.base}
              icon={<Plus />}
              size={isMobile ? "lg" : "sm"}
            >
              {t("my-subscriptions.addSubscription")}
            </ButtonLink>
          }
        />
      }
    >
      <div className="flex flex-col gap-gutter">
        <PortalSearchAndFilterMenu showSearchAndFilter={showSearchAndFilter} />
        {children}
      </div>
    </TabLayout>
  );
};
